// Privacy-first first-party analytics tracker. No cookies, no fingerprinting:
// a random per-tab session id (sessionStorage) plus a server-side daily-rotating
// visitor hash. Honors Do Not Track, and never records admins (anyone holding
// an unlocked admin token) or the /admin and /old sections.
import { API_URL } from '../../config';
import { normalizePath } from './seo';

const SESSION_KEY = 'ds_analytics_session';
const REFERRER_SENT_KEY = 'ds_analytics_ref_sent';
const OPTOUT_KEY = 'ds_analytics_optout';
const ADMIN_TOKEN_KEY = 'discerner_admin_token';

let currentPath = null;
let pageStartedAt = 0;
let listenersBound = false;
let lastTrackedPath = '';
let lastTrackedAt = 0;
// Deepest point reached on the current page, as % of page height.
let maxDepth = 0;
let depthTickPending = false;

const measureDepth = () => {
    depthTickPending = false;
    const doc = document.documentElement;
    const total = Math.max(1, doc.scrollHeight);
    const seen = Math.min(total, (window.scrollY || 0) + window.innerHeight);
    const depth = Math.round((seen / total) * 100);
    if (depth > maxDepth) {
        maxDepth = depth;
    }
};

const isDisabled = () => {
    try {
        return (
            navigator.doNotTrack === '1'
            || window.doNotTrack === '1'
            || localStorage.getItem(OPTOUT_KEY) === '1'
            || Boolean(sessionStorage.getItem(ADMIN_TOKEN_KEY))
        );
    } catch {
        return true;
    }
};

const getSessionId = () => {
    try {
        let id = sessionStorage.getItem(SESSION_KEY);
        if (!id) {
            const bytes = new Uint8Array(12);
            window.crypto.getRandomValues(bytes);
            id = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
            sessionStorage.setItem(SESSION_KEY, id);
        }
        return id;
    } catch {
        return '';
    }
};

const deviceClass = () => {
    const width = window.innerWidth || 1280;
    if (width < 768) return 'mobile';
    if (width < 1100) return 'tablet';
    return 'desktop';
};

// text/plain keeps the request preflight-free so sendBeacon works during
// page unload; the backend parses it explicitly.
const send = (payload) => {
    const url = `${API_URL}/api/analytics/collect`;
    const body = JSON.stringify(payload);
    try {
        if (navigator.sendBeacon
            && navigator.sendBeacon(url, new Blob([body], { type: 'text/plain' }))) {
            return;
        }
    } catch {
        // fall through to fetch
    }
    fetch(url, {
        method: 'POST',
        body,
        keepalive: true,
        headers: { 'Content-Type': 'text/plain' },
    }).catch(() => {});
};

// Records time spent on the page being left. `pause` keeps the current page
// active but stops the clock (used when the tab is hidden but may come back;
// bindLeaveTracking restarts the clock on return, so hidden time never counts
// as time-on-page).
export const flushLeave = (pause = false) => {
    if (!currentPath || !pageStartedAt) {
        return;
    }
    if (isDisabled()) {
        // e.g. the admin unlocked mid-session — stop recording immediately.
        currentPath = null;
        pageStartedAt = 0;
        return;
    }
    const duration = Math.round((Date.now() - pageStartedAt) / 1000);
    if (duration >= 1) {
        measureDepth();
        send({
            type: 'leave',
            path: currentPath,
            sessionId: getSessionId(),
            device: deviceClass(),
            duration: Math.min(duration, 1800),
            depth: maxDepth,
        });
    }
    if (pause) {
        pageStartedAt = 0;
    } else {
        currentPath = null;
        pageStartedAt = 0;
    }
};

// External referrer, reported once per session (document.referrer never
// changes during SPA navigation, so repeating it would inflate sources).
const referrerForFirstView = () => {
    try {
        if (sessionStorage.getItem(REFERRER_SENT_KEY)) {
            return '';
        }
        sessionStorage.setItem(REFERRER_SENT_KEY, '1');
        const referrer = document.referrer || '';
        if (!referrer) {
            return '';
        }
        const host = new URL(referrer).hostname;
        return host && host !== window.location.hostname ? referrer : '';
    } catch {
        return '';
    }
};

export const trackPageview = (pathname, searchQuery = '') => {
    if (isDisabled()) {
        return;
    }
    const sessionId = getSessionId();
    if (!sessionId) {
        return;
    }

    const path = normalizePath(pathname);
    if (!path || path.startsWith('/admin') || path.startsWith('/old')) {
        flushLeave();
        return;
    }

    // Guard against double-fired effects (React StrictMode in dev,
    // accidental re-mounts): the same path within 1s is one view. Re-arm the
    // timer if a flush (e.g. StrictMode's unmount cleanup) disarmed it.
    const now = Date.now();
    if (path === lastTrackedPath && now - lastTrackedAt < 1000) {
        if (!currentPath) {
            currentPath = path;
            pageStartedAt = now;
        }
        return;
    }
    lastTrackedPath = path;
    lastTrackedAt = now;
    flushLeave();

    currentPath = path;
    pageStartedAt = now;
    // New page, new depth measurement (initial viewport counts once the
    // reader scrolls or leaves).
    maxDepth = 0;

    const payload = {
        type: 'pageview',
        path,
        sessionId,
        device: deviceClass(),
        referrer: referrerForFirstView(),
    };
    if (path === '/search' && searchQuery) {
        payload.query = searchQuery;
    }
    send(payload);
};

export const bindLeaveTracking = () => {
    if (listenersBound) {
        return;
    }
    listenersBound = true;
    window.addEventListener('pagehide', () => flushLeave());
    window.addEventListener('scroll', () => {
        if (!depthTickPending) {
            depthTickPending = true;
            window.requestAnimationFrame(measureDepth);
        }
    }, { passive: true });
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
            flushLeave(true);
        } else if (currentPath && !pageStartedAt) {
            // Back from a hidden tab: restart the clock now so the hidden
            // interval is never counted as time on page.
            pageStartedAt = Date.now();
        }
    });
};
